/* eslint-disable max-lines-per-function */
import React, { useEffect, useRef, useState } from 'react';
import useStaffingRequest from './hooks/useStaffingRequest';
import StaffingRequestFilterUtility from './components/StaffingRequestFilterUtility';
import RequestTable from './components/RequestTable';
import StatusModal from './components/StatusModal';
import NewRequestPanel from './components/NewRequestPanel';
import { APPROVAL_STATUS_OPTIONS, REQUEST_STATUS_OPTIONS } from '../../../Constants/strings';
import './staffingRequest.scss';
import DeleteConfirm from './components/DeleteConfirm';
import { useLocation } from 'react-router-dom';
const StaffingRequestPage = () => {
  const initKeyRef = useRef('');
  const [isInitialized, setIsInitialized] = useState(false);
  const {
    filters,
    rows,
    options,
    errorMessage,
    selectedRequestIds,
    egmDetails,
    canShowEgm,
    canSelectRows,
    canOpenNewRequest,
    canDeleteRow,
    removeRequest,
    deletingIds,
    openApproveModalWithDefault,
    openCloseModalWithDefault,
    showBulkApproveModal,
    showBulkCloseModal,
    showApproveModal,
    showCloseModal,
    showNewRequest,
    projectInfo,
    draftRow,
    newRequestError,
    hasApprovalAccess,
    setShowBulkApproveModal,
    setShowBulkCloseModal,
    setShowNewRequest,
    setDraftRow,
    setFilterValue,
    metaLoaded,
    handleTableUtilityResponse,
    closeModals,
    submitBulkApprove,
    submitBulkClose,
    submitApprove,
    submitClose,
    approveDefaultStatus,
    closeDefaultStatus,
    checkEgm,
    toggleSelectRow,
    submitNewRequest,
    tableConfiguration,
    tableResetToken,
    getDeleteReason,
    isCreating,
    userProfile,
    isLoading,
  } = useStaffingRequest();
  // defensive fallback in case hot-reload left showCloseModal undefined
  const safeShowCloseModal = (typeof showCloseModal !== 'undefined') ? showCloseModal : false;

  const location = useLocation();

  useEffect(() => {
    const searchParams = new URLSearchParams(location.search || '');
    const queryProjectId = searchParams.get('projectId');
    const resolvedProjectId = location.state?.projectId || queryProjectId;
    const initKey = `${location.pathname}:${resolvedProjectId || ''}`;
    if (initKeyRef.current === initKey) return;
    initKeyRef.current = initKey;
    if (resolvedProjectId) {
      const isNumeric = /^\d+$/.test(String(resolvedProjectId).trim());
      const parsedProjectId = isNumeric ? Number(resolvedProjectId) : resolvedProjectId;
      setFilterValue('projectIds', [parsedProjectId]);
    }

    setIsInitialized(true);
  // eslint-disable-next-line
  }, [location.pathname, location.search, location.state?.projectId, setFilterValue]);

  // Only one project can be pre-filled/disabled in the new request panel
  const currentSelectedProjectId = filters.projectIds && filters.projectIds.length === 1
    ? filters.projectIds[0]
    : null;

  const openUpdateStatusModal = () => {
    if (!(typeof hasApprovalAccess === 'function' && (hasApprovalAccess('update') || hasApprovalAccess('edit')))) {
      return;
    }

    // Decide modal based on selected rows' approval state
    const selectedRows = rows.filter((row) => selectedRequestIds.includes(row.request_id));
    if (!selectedRows || selectedRows.length === 0) return;

    const anyOpenApproval = selectedRows.some((row) => Number(row.is_approved) === 0);
    const allApproved = selectedRows.every((row) => Number(row.is_approved) !== 0);

    const canApprovePerm = typeof hasApprovalAccess === 'function'
    && (hasApprovalAccess('update') || hasApprovalAccess('edit'));
    const canClosePerm = typeof hasApprovalAccess === 'function'
    && (hasApprovalAccess('update') || hasApprovalAccess('edit'));

    if (anyOpenApproval && canApprovePerm) {
      setShowBulkApproveModal(true);
      return;
    }

    if (allApproved && canClosePerm) {
      setShowBulkCloseModal(true);
      return;
    }

    // Mixed or permission-fallback: prefer approve modal if any approval is open and approval is allowed
    if (anyOpenApproval && !canApprovePerm && canClosePerm) {
      setShowBulkCloseModal(true);
      return;
    }

    // Final fallback use permissions
    if (canApprovePerm) setShowBulkApproveModal(true);
    else if (canClosePerm) setShowBulkCloseModal(true);
  };

  const [selectedToDelete, setSelectedToDelete] = useState(null);

  const handleCancelDelete = () => setSelectedToDelete(null);

  const handleOpenNewRequest = () => {
    if (!canOpenNewRequest) return;
    setShowNewRequest(true);
  };

  useEffect(() => {
    if (showNewRequest && !canOpenNewRequest) {
      setShowNewRequest(false);
    }
  }, [canOpenNewRequest, setShowNewRequest, showNewRequest]);

  const handleConfirmDelete = async (request) => {
    if (!request) return;
    try {
      await removeRequest(request);
    } finally {
      setSelectedToDelete(null);
    }
  };

  const isSelectedDeleting = Array.isArray(deletingIds)
    && selectedToDelete
    && deletingIds.includes(Number(selectedToDelete.request_id));

  return (
    <section className="sr-page">
      <div className="sr-shell-card">
        {errorMessage ? <p className="sr-error-text sr-error-inline">{errorMessage}</p> : null}

        {isInitialized ? (
          <StaffingRequestFilterUtility
            userProfile={userProfile}
            metaLoaded={metaLoaded}
            selectedCount={selectedRequestIds.length}
            canShowEgm={canShowEgm}
            egmDetails={egmDetails}
            onOpenUpdateStatus={openUpdateStatusModal}
            onCheckEgm={checkEgm}
            onOpenNewRequest={handleOpenNewRequest}
            canOpenNewRequest={canOpenNewRequest}
            hasApprovalAccess={hasApprovalAccess}
            options={options}
            filters={filters}
            tableResetToken={tableResetToken}
            onFilteredResponse={handleTableUtilityResponse}
          />
        ) : null}

        <div className="sr-table-card">
          <RequestTable
            rows={rows}
            isLoading={!isInitialized || isLoading}
            tableConfiguration={tableConfiguration}
            selectedRequestIds={selectedRequestIds}
            canSelectRows={canSelectRows}
            canDeleteRow={canDeleteRow}
            getDeleteReason={getDeleteReason}
            deletingIds={deletingIds}
            onDeleteRow={(row) => setSelectedToDelete(row)}
            onOpenApproval={openApproveModalWithDefault}
            onOpenClose={openCloseModalWithDefault}
            onToggleSelect={toggleSelectRow}
          />
        </div>
      </div>

      <NewRequestPanel
        isOpen={showNewRequest}
        projectInfo={projectInfo}
        currentSelectedProjectId={currentSelectedProjectId}
        options={options}
        draftRow={draftRow}
        error={newRequestError}
        isSubmitting={isCreating}
        onDraftChange={setDraftRow}
        onSubmit={submitNewRequest}
        onCancel={() => setShowNewRequest(false)}
      />

      <StatusModal
        isOpen={(hasApprovalAccess('update') || hasApprovalAccess('edit')) && showBulkApproveModal}
        title="Update Approval Status"
        projectName={projectInfo.project_name}
        selectedCount={selectedRequestIds.length}
        statusOptions={APPROVAL_STATUS_OPTIONS.slice(1)}
        defaultStatus={1}
        statusLabel="Approval Status"
        onSubmit={submitBulkApprove}
        onClose={closeModals}
      />

      {/* Single-approve modal (opened from row 'Open' action) */}
      <StatusModal
        isOpen={(hasApprovalAccess('update') || hasApprovalAccess('edit')) && showApproveModal}
        title="Update Approval Status"
        projectName={projectInfo.project_name}
        selectedCount={selectedRequestIds.length}
        statusOptions={APPROVAL_STATUS_OPTIONS.slice(1)}
        defaultStatus={approveDefaultStatus}
        statusLabel="Approval Status"
        onSubmit={submitApprove}
        onClose={closeModals}
      />

      {/* Single-close modal (opened from row 'Open' action) */}
      <StatusModal
        isOpen={(hasApprovalAccess('update') || hasApprovalAccess('edit'))
           && safeShowCloseModal}
        title="Update Request Status"
        projectName={projectInfo.project_name}
        selectedCount={selectedRequestIds.length}
        showCloseDate
        statusOptions={REQUEST_STATUS_OPTIONS}
        defaultStatus={closeDefaultStatus}
        onSubmit={submitClose}
        onClose={closeModals}
      />

      <StatusModal
        isOpen={(hasApprovalAccess('update') || hasApprovalAccess('edit')) && showBulkCloseModal}
        title="Update Request Status"
        projectName={projectInfo.project_name}
        selectedCount={selectedRequestIds.length}
        showCloseDate
        statusOptions={REQUEST_STATUS_OPTIONS}
        defaultStatus={1}
        onSubmit={submitBulkClose}
        onClose={closeModals}
      />
      <DeleteConfirm
        selectedRequest={selectedToDelete}
        onCancel={handleCancelDelete}
        onConfirm={handleConfirmDelete}
        isDeleting={isSelectedDeleting}
      />
    </section>
  );
};

export default StaffingRequestPage;
